import Entypo from '@expo/vector-icons/Entypo';
import {
  responsiveFontSize,
  responsiveHeight,
  responsiveWidth,
  useResponsiveFontSize,
  useResponsiveHeight,
} from '@/hooks/useResponsiveDimensions';
import shuffle from 'lodash/shuffle';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import colors from '@/config/colors';
import { AudioPlayer } from '@/components/AudioPlayer';
import { TextBody } from './textBody';
import { PressableNeolexon } from '@/components/PressableNeolexon';

export function SprechenTextExercise({
  id,
  showQuestions,
  back,
  skip,
  settings,
  exerciseState,
  currentExercise,
  exercise,
  audioUrl,
  checkProgress,
}) {
  const { bottom, left, right, top } = useSafeAreaInsets();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const responsiveIconSize = useResponsiveFontSize(4);
  const scrollViewRef = useRef(null);

  const horizontalSafeAreaInsets = left + right;
  const verticalSafeAreaInsets = top + bottom;
  const { questions, type } = exercise;
  const { setting, value, playbackRate } = settings;
  const modality = type === 'Sprechen' ? 'speak' : 'not-speak';
  const showBack = exerciseState === 'questions';
  const scrollViewHeight = responsiveHeight(62) - verticalSafeAreaInsets;
  const [scrollPosition, setScrollPosition] = useState(0);
  const responsiveLineHeight = useResponsiveFontSize(2.7);

  const responsiveStyles = StyleSheet.create({
    bodyContainer: {
      height: scrollViewHeight,
      width: responsiveWidth(98) - horizontalSafeAreaInsets,
    },
    buttonsContainer: {
      height: responsiveHeight(15) - verticalSafeAreaInsets,
      width: responsiveWidth(90) - horizontalSafeAreaInsets,
    },
    scrollViewContainer: {
      height: scrollViewHeight,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.23,
      shadowRadius: 2.62,
      elevation: 4,
      backgroundColor: colors.white,
    },
    scrollViewAudioContainer: {
      height: scrollViewHeight,
      width: '100%',
      opacity: 1,
    },
    audioPlayerContainer: {
      marginBottom: useResponsiveHeight(1),
      width: '100%',
      alignItems: 'center',
    },
  });

  const neededQuestions = useMemo(() => {
    let returnValue;
    if (modality !== 'speak') {
      returnValue = questions.filter(function (question) {
        return question.questionType === setting;
      });
    } else {
      if (setting === 'free') {
        returnValue = [
          {
            questionText: 'Erzählen Sie die Geschichte nach.',
            questionAudio: '/audio/erzaehlen_sie_nach.mp3',
          },
        ];
      } else {
        returnValue = questions.filter(function (question) {
          return question.questionText !== 'Erzählen Sie die Geschichte nach.';
        });
      }
    }
    // shuffle order of choices and reduce number of choices according to setting
    if (setting === 'closed') {
      returnValue.forEach(function (question) {
        shuffle(question.choices);
        // calculate how many choices need to be removed to match setting
        let elementsToRemove = question.choices.length - value;
        question.choices = question.choices.filter(function (choice) {
          if (choice.isCorrect === true || elementsToRemove < 1) {
            return true;
          } else {
            elementsToRemove--;
            return false;
          }
        });
        shuffle(question.choices);
      });
    }
    // shuffle order of questions
    shuffle(returnValue);
    return returnValue;
  }, [JSON.stringify(questions), setting, value]);

  const maxNumberOfQuestionIndex = neededQuestions?.length - 1;
  const maxIterations = questionIndex === maxNumberOfQuestionIndex;

  const textContent = useMemo(() => {
    return exercise.textContent.replace(/\[|\]/g, '');
  }, [exercise.textContent]);

  const onWeiterClick = () => {
    if (showBack) {
      if (maxIterations) {
        return skip();
      }
      checkProgress(false, true, true);
      setQuestionIndex((prevValue) => prevValue + 1);
    }
    showQuestions();
  };

  const handleScroll = (event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const subtractor = Platform.OS === 'android' ? 80 : 20;
    setScrollPosition(contentOffset.y);
    const isScrolledToEnd =
      Math.ceil(layoutMeasurement.height + contentOffset.y) >=
      contentSize.height - subtractor;
    setIsScrolledToEnd(isScrolledToEnd);
  };

  const scrollDown = (withAnim = true) => {
    if (isScrolledToEnd) {
      scrollViewRef.current.scrollToEnd({ animated: withAnim });
    } else {
      scrollViewRef.current.scrollTo({
        y: scrollPosition + 7 * responsiveLineHeight,
        animated: true,
      });
    }
  };

  const scrollUp = () => {
    scrollViewRef.current.scrollTo({
      y: scrollPosition - 7 * responsiveLineHeight,
      animated: true,
    });
  };

  const scrollUpAfterGoingBack = useCallback(() => {
    back();
  }, []);

  useEffect(() => {
    if (!showBack && isScrolledToEnd) {
      scrollDown(false);
    }
  }, [showBack, isScrolledToEnd]);

  useEffect(() => {
    setQuestionIndex(0);
    setScrollPosition(0);
    setIsScrolledToEnd(false);
  }, [currentExercise]);

  return (
    <SafeAreaView style={styles.container}>
      <View style={[styles.bodyContainer, responsiveStyles.bodyContainer]}>
        {showBack ? (
          <View style={styles.textBodyContainer}>
            <TextBody
              id={id}
              questions={neededQuestions}
              questionIndex={questionIndex}
              isMiniPlayer
              playbackRate={playbackRate}
            />
          </View>
        ) : (
          <View style={responsiveStyles.scrollViewAudioContainer}>
            <View style={responsiveStyles.audioPlayerContainer}>
              <AudioPlayer
                isTracked
                uniqueKey={exercise.uniqueExercisePrepareDate ?? null}
                source={audioUrl}
                playbackRate={playbackRate}
              />
            </View>
            <ScrollView
              key={currentExercise}
              onContentSizeChange={(w, h) => {
                if (scrollViewHeight >= h) {
                  setIsScrolledToEnd(true);
                }
              }}
              onScroll={handleScroll}
              ref={scrollViewRef}
              contentContainerStyle={styles.scrollViewContentContainer}
              style={responsiveStyles.scrollViewContainer}
            >
              <View style={styles.titleContainer}>
                <Text style={styles.titleText}>{exercise.title}</Text>
              </View>
              <Text style={styles.textContent}>{textContent}</Text>
            </ScrollView>
          </View>
        )}
      </View>
      <View
        style={[styles.buttonsContainer, responsiveStyles.buttonsContainer]}
      >
        {showBack ? (
          <PressableNeolexon
            onPress={scrollUpAfterGoingBack}
            style={styles.weiterContainer}
          >
            <Entypo
              name="text-document-inverted"
              size={responsiveIconSize}
              color={colors.darkGrey}
            />
          </PressableNeolexon>
        ) : (
          <PressableNeolexon
            onPress={scrollUp}
            disabled={scrollPosition === 0}
            style={[styles.weiterContainer, scrollPosition === 0 && styles.dim]}
          >
            <Entypo
              name="arrow-bold-left"
              size={responsiveIconSize}
              color={colors.black}
            />
          </PressableNeolexon>
        )}

        <PressableNeolexon
          onPress={isScrolledToEnd ? onWeiterClick : scrollDown}
          style={styles.weiterContainer}
        >
          <Text
            style={[
              styles.responsiveText,
              isScrolledToEnd && styles.weiterStyle,
            ]}
          >
            weiter
          </Text>
          <Entypo
            name="arrow-bold-right"
            size={responsiveIconSize}
            color={isScrolledToEnd ? colors.primary : colors.black}
          />
        </PressableNeolexon>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: responsiveHeight(1),
    justifyContent: 'center',
    alignItems: 'center',
  },
  bodyContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: responsiveHeight(1),
  },
  weiterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  weiterStyle: {
    color: colors.primary,
  },
  responsiveText: {
    fontSize: responsiveFontSize(3),
    fontWeight: '700',
    color: colors.black,
  },
  scrollViewContentContainer: {
    paddingVertical: 30,
    paddingHorizontal: 40,
  },
  titleContainer: {
    justifyContent: 'center',
    marginBottom: 10,
  },
  titleText: {
    fontSize: responsiveFontSize(2.4),
    fontWeight: '700',
  },
  textContent: {
    fontSize: responsiveFontSize(2.2),
    fontWeight: '400',
    color: colors.black,
    lineHeight: Platform.OS === 'web' ? 60 : 45,
  },
  textBodyContainer: { flex: 1, opacity: 1 },
  dim: { opacity: 0.4 },
});
